import { mondayOf } from '@/lib/calendar/calendar-grid'
import { toISODate } from '@/lib/content/content-period'
import type { InboxReadMarks, InboxThread } from '@/types/inbox'

/** The `?item=` value that opens the note composer in place of a thread. */
export const NEW_NOTE_ID = 'new'

/** The day groups under the flagged threads. */
export type InboxDayGroup = 'today' | 'thisWeek' | 'earlier'

/** A thread as a list row shows it: the thread and whether the member has read it. */
export interface InboxListEntry {
  readonly thread: InboxThread
  readonly unread: boolean
}

export interface InboxLayout {
  readonly flagged: readonly InboxListEntry[]
  readonly groups: readonly {
    readonly key: InboxDayGroup
    readonly items: readonly InboxListEntry[]
  }[]
}

/** Whether a thread has an entry newer than the member's read mark, or has no read mark at all. */
export function unread(thread: InboxThread, readMarks: InboxReadMarks): boolean {
  const readAt = readMarks.get(thread.id)
  return readAt === undefined || thread.lastEntryAt.getTime() > readAt.getTime()
}

function dayGroupOf(at: Date, now: Date): InboxDayGroup {
  const day = toISODate(at)
  if (day >= toISODate(now)) return 'today'
  return day >= toISODate(mondayOf(now)) ? 'thisWeek' : 'earlier'
}

/** The threads that need action first, then Today, This week and Earlier, each newest first. */
export function layoutInbox(
  threads: readonly InboxThread[],
  readMarks: InboxReadMarks,
  now: Date,
): InboxLayout {
  const sorted = [...threads].sort((a, b) => b.lastEntryAt.getTime() - a.lastEntryAt.getTime())
  const order: InboxDayGroup[] = ['today', 'thisWeek', 'earlier']
  const flagged: InboxListEntry[] = []
  const byGroup = new Map<InboxDayGroup, InboxListEntry[]>()
  for (const thread of sorted) {
    const entry = { thread, unread: unread(thread, readMarks) }
    if (thread.actionNeeded) {
      flagged.push(entry)
      continue
    }
    const key = dayGroupOf(thread.lastEntryAt, now)
    const bucket = byGroup.get(key)
    if (bucket) bucket.push(entry)
    else byGroup.set(key, [entry])
  }
  return {
    flagged,
    groups: order.flatMap((key) => {
      const bucket = byGroup.get(key)
      return bucket ? [{ key, items: bucket }] : []
    }),
  }
}

/** The rows in the order the list shows them. */
export function orderedItems(layout: InboxLayout): InboxListEntry[] {
  return [...layout.flagged, ...layout.groups.flatMap((group) => group.items)]
}

/** The row `step` places away from `currentId`, clamped to the ends; the first row when nothing is selected. */
export function neighbourId(
  ids: readonly string[],
  currentId: string | null,
  step: number,
): string | null {
  if (ids.length === 0) return null
  const index = currentId === null ? -1 : ids.indexOf(currentId)
  if (index === -1) return ids[0] ?? null
  return ids[Math.min(ids.length - 1, Math.max(0, index + step))] ?? null
}
